import { useTheme } from '@automata/mui-theme';
import { logger } from '@automata/utils';
import { useEffect } from 'react';
import {
  BaseEdge,
  type EdgeProps,
  Position,
  getSmoothStepPath,
} from 'reactflow';

import type { TimeConstraintEdgeData } from '../../../types';

const STROKE_WIDTH = 3;
const DASH_SPACING = 8;

export const TimeConstraintEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  data,
}: EdgeProps<TimeConstraintEdgeData>) => {
  const theme = useTheme();

  const labwareCorrespondence =
    data?.labwareCorrespondence ?? 'NO_LABWARE_ASSIGNED';

  const showErrorState =
    data?.errors !== undefined &&
    labwareCorrespondence !== 'NO_LABWARE_ASSIGNED';

  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition: Position.Right,
    targetPosition: Position.Right,
  });

  useEffect(() => {
    if (data === undefined) {
      logger.error(`no data for time constraint ${id}`);
      return;
    }
    data.onMidpointChange?.(id, { x: labelX, y: labelY });
  }, [data, id, labelX, labelY]);

  const isDashed = labwareCorrespondence === 'NO_LABWARE_ASSIGNED';
  const edgeStyle = {
    stroke: showErrorState
      ? theme.palette.error.main
      : theme.palette.common.black,
    strokeWidth: STROKE_WIDTH,
    strokeDasharray: isDashed ? DASH_SPACING : undefined,
    strokeLinecap: 'round',
  } as const;

  if (data?.iconPosition !== undefined) {
    const [pathToIcon] = getSmoothStepPath({
      sourceX,
      sourceY,
      targetX: data.iconPosition.x,
      targetY: data.iconPosition.y,
      sourcePosition: Position.Right,
      targetPosition: Position.Right,
    });
    const [pathFromIcon] = getSmoothStepPath({
      sourceX: data.iconPosition.x,
      sourceY: data.iconPosition.y,
      targetX,
      targetY,
      sourcePosition: Position.Right,
      targetPosition: Position.Right,
    });

    return (
      <>
        <BaseEdge data-testid="base-edge" path={pathToIcon} style={edgeStyle} />
        <BaseEdge path={pathFromIcon} style={edgeStyle} />
      </>
    );
  }

  return <BaseEdge data-testid="base-edge" path={edgePath} style={edgeStyle} />;
};
